import React, { useMemo } from "react";
import { Button, Space, Tag, Tooltip } from "antd";
import { InfoCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Job } from "api/Reviews";
import { Task } from "../tasks";
import {
  WAITING_JOBS_COLUMNS,
  DELAYED_JOBS_COLUMNS,
  COMPLETED_JOBS_COLUMNS,
  TASKS_COLUMNS,
  JOB_NAME_DISPLAY_MAP,
  QUEUE_DISPLAY_NAMES,
  TAG_CONFIG,
  TEXT_CONFIG,
  BUTTON_CONFIG,
  ICON_CONFIG,
} from "../config";

interface TableColumnsReturn {
  waitingJobsColumns: any[];
  delayedJobsColumns: any[];
  completedJobsColumns: any[];
  tasksColumns: any[];
}

export const useTableColumns = (
  lastRunTimes: Record<string, string>,
  onTriggerTask?: (task: Task) => void,
  onRemoveJob?: (job: Job) => Promise<void>,
): TableColumnsReturn => {
  const getJobNameDisplay = (name: string) => {
    return JOB_NAME_DISPLAY_MAP[name] || name;
  };

  const getJobStatusTag = (job: Job) => {
    if (job.failedReason) {
      const config = TAG_CONFIG.jobStatus.error;
      return (
        <Tag color={config.color} icon={<config.icon />}>
          {config.text}
        </Tag>
      );
    }
    if (job.finishedOn) {
      const config = TAG_CONFIG.jobStatus.completed;
      return (
        <Tag color={config.color} icon={<config.icon />}>
          {config.text}
        </Tag>
      );
    }
    const config = TAG_CONFIG.jobStatus.waiting;
    return (
      <Tag color={config.color} icon={<config.icon />}>
        {config.text}
      </Tag>
    );
  };

  const formatDelay = (delayMs: number) => {
    if (!delayMs) return TEXT_CONFIG.noPriority;
    const minutes = Math.round(delayMs / 60000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      return rest ? `${hours} ч ${rest} мин` : `${hours} ч`;
    }
    return `${minutes} мин`;
  };

  const waitingJobsColumns = useMemo(
    () =>
      WAITING_JOBS_COLUMNS.map((column) => ({
        ...column,
        render:
          column.dataIndex === "name"
            ? (name: string) => getJobNameDisplay(name)
            : column.dataIndex === "queue"
            ? (queue: string) => QUEUE_DISPLAY_NAMES[queue] || queue || "—"
            : column.dataIndex === "data"
            ? (data: any) => (data ? JSON.stringify(data) : TEXT_CONFIG.noData)
            : column.dataIndex === "timestamp"
            ? (timestamp: number) => new Date(timestamp).toLocaleString()
            : column.dataIndex === "priority"
            ? (priority: number) => priority || TEXT_CONFIG.noPriority
            : column.key === "actions"
            ? (_: unknown, record: Job) =>
                record.queue != null && onRemoveJob ? (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveJob(record)}
                  >
                    {BUTTON_CONFIG.removeJob.text}
                  </Button>
                ) : null
            : undefined,
      })),
    [onRemoveJob]
  );

  const delayedJobsColumns = useMemo(
    () =>
      DELAYED_JOBS_COLUMNS.map((column) => ({
        ...column,
        render:
          column.dataIndex === "name"
            ? (name: string) => getJobNameDisplay(name)
            : column.dataIndex === "queue"
            ? (queue: string) => QUEUE_DISPLAY_NAMES[queue] || queue || "—"
            : column.dataIndex === "data"
            ? (data: any) => (data ? JSON.stringify(data) : TEXT_CONFIG.noData)
            : column.dataIndex === "timestamp" || column.dataIndex === "runAt"
            ? (value: number) =>
                value ? new Date(value).toLocaleString() : TEXT_CONFIG.noData
            : column.dataIndex === "delay"
            ? (delay: number) => formatDelay(delay)
            : column.key === "actions"
            ? (_: unknown, record: Job) =>
                record.queue != null && onRemoveJob ? (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveJob(record)}
                  >
                    {BUTTON_CONFIG.removeJob.text}
                  </Button>
                ) : null
            : undefined,
      })),
    [onRemoveJob]
  );

  const completedJobsColumns = useMemo(
    () =>
      COMPLETED_JOBS_COLUMNS.map((column) => ({
        ...column,
        render:
          column.dataIndex === "name"
            ? (name: string) => getJobNameDisplay(name)
            : column.dataIndex === "queue"
            ? (queue: string) => QUEUE_DISPLAY_NAMES[queue] || queue || "—"
            : column.key === "status"
            ? (job: Job) => getJobStatusTag(job)
            : column.dataIndex === "timestamp"
            ? (timestamp: number) => new Date(timestamp).toLocaleString()
            : column.dataIndex === "finishedOn"
            ? (finishedOn: number) =>
                finishedOn
                  ? new Date(finishedOn).toLocaleString()
                  : TEXT_CONFIG.noFinishedTime
            : column.dataIndex === "failedReason"
            ? (failedReason: string) => failedReason || TEXT_CONFIG.noError
            : undefined,
      })),
    []
  );

  const tasksColumns = useMemo(
    () =>
      TASKS_COLUMNS.map((column) => ({
        ...column,
        render:
          column.dataIndex === "name"
            ? (text: string, record: Task) => (
                <Space>
                  {text}
                  <Tooltip title={record.description}>
                    <InfoCircleOutlined style={ICON_CONFIG.info.style} />
                  </Tooltip>
                </Space>
              )
            : column.dataIndex === "jobName"
            ? (jobName: string) =>
                lastRunTimes[jobName]
                  ? new Date(lastRunTimes[jobName]).toLocaleString("ru-RU")
                  : TEXT_CONFIG.noData
            : column.key === "actions"
            ? (_: unknown, record: Task) => (
                <Button
                  icon={<BUTTON_CONFIG.triggerTask.icon />}
                  onClick={() => onTriggerTask?.(record)}
                >
                  {BUTTON_CONFIG.triggerTask.text}
                </Button>
              )
            : undefined,
      })),
    [lastRunTimes, onTriggerTask]
  );

  return {
    waitingJobsColumns,
    delayedJobsColumns,
    completedJobsColumns,
    tasksColumns,
  };
};
